/**
 * When a background-job status check fails, should the poller wait it out?
 *
 * Background jobs (tenant export, import preview, tenant import, content
 * import) live in the memory of the API server that started them. dev01 runs
 * one API server; qa, uat, integ and prod run two or more behind a load
 * balancer that spreads requests across them one request at a time. A status
 * check that lands on another server answers 404 `job_not_found` while the job
 * is running fine on its owner, and the next checks reach the owner again.
 *
 * So a 404 is a failure only once no check has reached the owner for
 * `JOB_NOT_FOUND_GRACE_MS` (three minutes of 2-3 s checks: with two servers the
 * chance that none of them lands on the owner is far below one in a billion).
 * Any other failure (network blip, a 502/503/504 from the proxy) keeps the
 * earlier rule: give up after `MAX_CONSECUTIVE_OTHER_POLL_ERRORS` in a row.
 * A check that reaches the job resets both.
 */
export const JOB_NOT_FOUND_GRACE_MS = 3 * 60 * 1000;
export const MAX_CONSECUTIVE_OTHER_POLL_ERRORS = 5;

export interface PollMiss {
  /** True when the poller should stop waiting and report the job as lost. */
  giveUp: boolean;
  /** The server's error code or the request error, for the message shown. */
  reason: string;
  /** True when the check reached a server that does not hold the job (HTTP 404). */
  notFound: boolean;
}

export interface PollMissTracker {
  /** Call after every check that reached the job. */
  reached(): void;
  /** Call with the error of every failed check. */
  missed(err: unknown): PollMiss;
}

export function createPollMissTracker(opts: {
  notFoundGraceMs?: number;
  maxOtherErrors?: number;
  now?: () => number;
} = {}): PollMissTracker {
  const graceMs = opts.notFoundGraceMs ?? JOB_NOT_FOUND_GRACE_MS;
  const maxOther = opts.maxOtherErrors ?? MAX_CONSECUTIVE_OTHER_POLL_ERRORS;
  const now = opts.now ?? Date.now;
  let notFoundSince: number | null = null;
  let otherErrors = 0;
  return {
    reached() {
      notFoundSince = null;
      otherErrors = 0;
    },
    missed(err: unknown): PollMiss {
      const e = err as { response?: { status?: number; data?: { error?: string } }; message?: string } | null;
      const reason = e?.response?.data?.error || e?.message || 'poll failed';
      if (e?.response?.status === 404) {
        if (notFoundSince === null) notFoundSince = now();
        return { giveUp: now() - notFoundSince >= graceMs, reason, notFound: true };
      }
      otherErrors += 1;
      return { giveUp: otherErrors >= maxOther, reason, notFound: false };
    },
  };
}

/** The user-facing sentence for a job no check could reach. */
export function lostJobMessage(kind: string, miss: PollMiss): string {
  return miss.notFound
    ? `Lost contact with the ${kind} job: no status check reached the server running it for ${JOB_NOT_FOUND_GRACE_MS / 60000} minutes (${miss.reason}). It may still finish in the background — check again shortly.`
    : `Lost contact with the ${kind} job after ${MAX_CONSECUTIVE_OTHER_POLL_ERRORS} failed checks in a row (${miss.reason}). It may still finish in the background — check again shortly.`;
}
